import { useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Route, Switch, Redirect, useLocation } from 'react-router-dom'

import { ExternalEventEmitter } from '@/ExternalEventEmitter'
import { parseJoinRoomLink } from '@/services/JoinRoomLink'
import useRootSnackbar from '@/services/useRootSnackbar'
import ElectronIPC from '@/services/ElectronIPC'

import EgressPage from '@/pages/EgressPage/EgressPage'
import RegisterUserPage from '@/pages/UnauthorizedPages/RegisterUserPage'
import LoginPage from '@/pages/UnauthorizedPages/LoginPage'
import PasswordResetPage from '@/pages/UnauthorizedPages/PasswordResetPage'
import PasswordResetResultPage from '@/pages/UnauthorizedPages/PasswordResetResultPage'
import { GuestJoinPage } from '@/pages/UnauthorizedPages/GuestJoinPage'
import { ExternalEvent } from '@/AppContext'
import { env } from './services/EnvironmentVariables'

function UnauthenticatedApp() {
    const location = useLocation()

    useEffect(() => {
        ElectronIPC.setMenu({ menuName: 'loggedout' })
    }, [])

    const { code } = parseJoinRoomLink(window.location.toString())
    const redirectToGuestJoin = code && !env.IS_ELECTRON

    return (
        <>
            <AnimatePresence mode="wait" initial={false}>
                <Switch location={location} key={location.pathname}>
                    <Route key={'/login'} path={'/login'} exact={true}>
                        <LoginPage />
                    </Route>
                    <Route key={'/guest-join'} path={'/guest-join'} exact={true}>
                        <GuestJoinPage />
                    </Route>
                    <Route key={'/register'} path={'/register'} exact={true}>
                        <RegisterUserPage />
                    </Route>
                    <Route key={'/reset-password'} path={'/reset-password'} exact={true}>
                        <PasswordResetPage />
                    </Route>
                    <Route key={'/reset-password-result'} path={'/reset-password-result'} exact={true}>
                        <PasswordResetResultPage />
                    </Route>
                    <Route key={'/pages/egress'} path={'/pages/egress'} exact={true}>
                        <EgressPage />
                    </Route>
                    <Redirect
                        to={{
                            pathname: redirectToGuestJoin ? '/guest-join' : '/login',
                            search: redirectToGuestJoin
                                ? '?' + new URL(window.location.toString()).searchParams.toString()
                                : undefined,
                        }}
                    />
                </Switch>
            </AnimatePresence>
            <JoinRoomExternalEventHandler />
        </>
    )
}

function JoinRoomExternalEventHandler() {
    const RootSnackbar = useRootSnackbar()

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'join-room-link') {
                const { error, code } = parseJoinRoomLink(externalEvent.value)
                if (code) {
                    RootSnackbar.show({
                        severity: 'info',
                        message: 'Please log in before attempting to join the room.',
                    })
                }
                if (error) {
                    RootSnackbar.show({
                        severity: 'error',
                        message: 'Failed to parse join room link: ' + error.message,
                    })
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    return <></>
}

export default UnauthenticatedApp
